import { UNLIMITED_DAILY_QUOTA } from '../constants';
import { shouldCheckNavigation } from '../helper/rules';
import { now } from '../lib/dateTime';
import { getDeviceAndUserStore, getRulesStore } from '../storage/stores';
import {
  getConsumedQuotaSeconds,
  getEffectiveDailyQuotaMinutes,
} from '../validators/timeQuotaValidator';
import { DeviceAndUserStore } from '../types/store';

export const DailyQuotaRuleStateType = {
  NO_LIMIT: 'noLimit',
  QUOTA_EXCEEDED: 'quotaExceeded',
  TIME_LEFT: 'timeLeft',
} as const;

export type DailyQuotaRuleState =
  | { readonly state: typeof DailyQuotaRuleStateType.NO_LIMIT }
  | { readonly state: typeof DailyQuotaRuleStateType.QUOTA_EXCEEDED }
  | {
      readonly state: typeof DailyQuotaRuleStateType.TIME_LEFT;
      readonly secondsLeft: number;
    };

const noLimit = (): DailyQuotaRuleState => ({
  state: DailyQuotaRuleStateType.NO_LIMIT,
});

const quotaExceeded = (): DailyQuotaRuleState => ({
  state: DailyQuotaRuleStateType.QUOTA_EXCEEDED,
});

const timeLeft = (secondsLeft: number): DailyQuotaRuleState => ({
  state: DailyQuotaRuleStateType.TIME_LEFT,
  secondsLeft,
});

export type DailyQuotaRuleCheckerDeps = {
  getRulesStore: typeof getRulesStore;
  getDeviceAndUserStore: typeof getDeviceAndUserStore;
  now: () => Promise<number>;
  getConsumedQuotaSeconds: (deviceUserData: DeviceAndUserStore) => number;
};

export type DailyQuotaRuleChecker = {
  check: (date?: number) => Promise<DailyQuotaRuleState>;
};

/**
 * Keeps quota decisions shared across blockers and notifications without
 * coupling tests to global stores or the clock.
 */
export const createDailyQuotaRuleChecker = (
  deps: DailyQuotaRuleCheckerDeps
): DailyQuotaRuleChecker => {
  const check = async (date?: number): Promise<DailyQuotaRuleState> => {
    const deviceUser = deps.getDeviceAndUserStore().get();
    const rulesStore = deps.getRulesStore();
    const timeRestrictions = rulesStore.getTimeRestrictions();
    const timezone = rulesStore.getTimezone();

    if (
      !deviceUser ||
      !timeRestrictions ||
      !timezone ||
      !shouldCheckNavigation(timeRestrictions)
    ) {
      return noLimit();
    }

    const effectiveDailyQuotaMinutes = getEffectiveDailyQuotaMinutes(
      timeRestrictions,
      rulesStore.getDailyQuota(),
      date ?? (await deps.now()),
      timezone
    );

    // TypeScript cannot infer the earlier `timeRestrictions` guard through the
    // quota helper, so this keeps the public return type explicit.
    if (
      effectiveDailyQuotaMinutes === undefined ||
      effectiveDailyQuotaMinutes === UNLIMITED_DAILY_QUOTA
    ) {
      return noLimit();
    }

    const secondsLeft =
      effectiveDailyQuotaMinutes * 60 -
      deps.getConsumedQuotaSeconds(deviceUser);

    if (secondsLeft <= 0) {
      return quotaExceeded();
    }

    return timeLeft(secondsLeft);
  };

  return { check };
};

/**
 * Application quota checker backed by persisted rules, device state, and time.
 */
export const dailyQuotaRuleChecker: DailyQuotaRuleChecker =
  createDailyQuotaRuleChecker({
    getRulesStore,
    getDeviceAndUserStore,
    now,
    getConsumedQuotaSeconds,
  });
